import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { adminApi, publicApi, session, type Incident, type StatusResponse } from "../../lib/api";
import { StatusPill } from "../../components/StatusPill";

export function AdminDashboard() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [active, setActive] = useState<Incident[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!session.get()) {
      navigate("/admin/login", { replace: true });
      return;
    }
    Promise.all([publicApi.status(), publicApi.activeIncidents()])
      .then(([s, i]) => {
        setStatus(s);
        setActive(i.incidents);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : "unknown"));
  }, [navigate]);

  async function resolve(incidentId: string) {
    const note =
      window.prompt("Resolution note (will appear in the feed):", "Resolved.") ??
      "Resolved.";
    try {
      await adminApi.resolveIncident(incidentId, note);
      const i = await publicApi.activeIncidents();
      setActive(i.incidents);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "unknown");
    }
  }

  if (err) {
    return (
      <section className="mx-auto max-w-4xl px-4 py-10">
        <p>Error loading dashboard: {err}</p>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-4xl px-4 py-10">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Admin dashboard</h1>
        <div className="flex gap-3">
          <Link
            to="/admin/incidents/new"
            className="rounded bg-slate-900 px-4 py-2 text-sm text-white"
          >
            Open new incident
          </Link>
          <button
            onClick={() => {
              session.clear();
              navigate("/admin/login");
            }}
            className="rounded border border-slate-300 px-3 py-2 text-sm"
          >
            Sign out
          </button>
        </div>
      </header>

      <section className="mt-8">
        <h2 className="text-lg font-semibold">Active incidents</h2>
        {active.length === 0 ? (
          <p className="mt-3 text-slate-600">No active incidents.</p>
        ) : (
          <ul className="mt-3 space-y-3">
            {active.map((inc) => (
              <li
                key={inc.incident_id}
                className="rounded border border-slate-200 bg-white p-4"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium">{inc.title}</div>
                    <p className="text-xs text-slate-500">
                      {inc.severity} &middot; {inc.status} &middot;{" "}
                      {inc.affected_components.join(", ")}
                    </p>
                  </div>
                  <button
                    className="rounded bg-green-600 px-3 py-1 text-sm text-white"
                    onClick={() => resolve(inc.incident_id)}
                  >
                    Resolve
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {status && (
        <section className="mt-8">
          <h2 className="text-lg font-semibold">Component states</h2>
          <ul className="mt-3 divide-y divide-slate-200 rounded border border-slate-200 bg-white">
            {[...status.components.core, ...status.components.upstream].map(
              (c) => (
                <li
                  key={c.component_id}
                  className="flex items-center justify-between px-4 py-2"
                >
                  <span>{c.name}</span>
                  <StatusPill status={c.status} />
                </li>
              ),
            )}
          </ul>
        </section>
      )}
    </section>
  );
}
